// #region imports

// --- Angular
import { HttpClient } from "@angular/common/http";

// --- RxJs
import { catchError } from "rxjs";

// --- 3rd Party
import { JL } from 'jsnlog';
import { TranslateService } from "@ngx-translate/core";
import { retryBackoff } from "backoff-rxjs";

// --- HS Code
import { environment } from "../../../../environments/environment";
import { ApplicationError, CommandResult, GroupModel, RespondentModel } from "../../../models";
import { AppService } from "../app.service";
import { CommandTypes } from "../../../models/shared/command-types";
import { CommandHandlerResult } from "../system/command-handler";
import { DeleteRespondentCommand } from "./delete-respondent-command";

// #endregion
export class MoveRespondentCommand  extends DeleteRespondentCommand {

  private _targetGroup: GroupModel;

  constructor(
    _logger: JL.JSNLog,
    groups: GroupModel[],
    respondent: RespondentModel,
    targetGroup: GroupModel,
    _appService: AppService,
    _httpClient: HttpClient,
    _translateService: TranslateService
  ) {
    super(_logger, groups, respondent, _appService, _httpClient, _translateService);
    this._groups = groups;
    this._respondent = respondent;
    this._targetGroup = targetGroup;
  }

  override execute(): void {
    if (this._executionStatus !== this._COMMAND_STATE_NOT_EXECUTED) return;
    if (!this._respondent || !this._targetGroup) return;
    if (this._respondent.groupId.toLowerCase() === this._targetGroup.id.toLowerCase()) return;

    const theGroup = this._groups.find((group: GroupModel) => group.id.toLowerCase() === this._respondent.groupId.toLowerCase());
    if (!theGroup) return;

    this._executionStatus = this._COMMAND_STATE_EXECUTING;

    let newGroupIndex: number = 1;
    const setRespondents = this._targetGroup.respondents.filter(r => r.isSet);

    if (setRespondents.length > 0) {
      newGroupIndex = Math.max(...setRespondents.map(r => r.groupIndex)) + 1;
    }

    const respondentsToReIndex = theGroup.respondents
      .filter((r: RespondentModel) => r.groupIndex > this._respondent.groupIndex && r.isSet);

    const dtos: any[] = [];
    dtos.push({
      commandId: ++MoveRespondentCommand._commandId,
      identifier: this._respondent.id,
      commandType: CommandTypes.update,
      target: this._respondent,
      propertyNames: ['groupId', 'groupIndex'],
      newValues: [this._targetGroup.id, newGroupIndex]
    });
    respondentsToReIndex.forEach(r => dtos.push({
      commandId: ++MoveRespondentCommand._commandId,
      identifier: r.id,
      commandType: CommandTypes.update,
      target: r,
      propertyNames: ['groupIndex'],
      newValues: [r.groupIndex - 1]
    }));

    this._appService.setLoading(true);

    this._httpClient.post<CommandResult[]>(this._apiUri, dtos).pipe(
      retryBackoff(environment.retryConfig),
      catchError(this._error.bind(this))
    ).subscribe((results: CommandResult[] | CommandHandlerResult) => {

      this._appService.setLoading(false);

      if (Array.isArray(results)) {
        const isSuccess = results.every(r => r.isSuccessful);
        const errorMessage = isSuccess ? '' : this._translateService.instant('v2GeneralCommandErrorMessage');
        if (!isSuccess) {
          this._commandResult$.next([]);
          this._executionStatus = this._COMMAND_STATE_ERROR;
          this._appService.setLastError({ errorMessage: errorMessage } as ApplicationError);
          this._executionStatus = this._COMMAND_STATE_ERROR;

          return;
        }
      }

      const targetGroup = this._moveRespondentIntoTheTargetGroup(this._targetGroup, newGroupIndex);
      const sourceGroup = this._removeRespondentFromSourceGroup(this._groups.find(g => g.id.toLowerCase() === this._respondent.groupId.toLowerCase())!);
      const targetGroupIndex = this._groups.findIndex(g => g.id.toLowerCase() === targetGroup.id.toLowerCase());
      this._groups.splice(targetGroupIndex, 1, targetGroup);
      const sourceGroupIndex = this._groups.findIndex(g => g.id.toLowerCase() === sourceGroup.id.toLocaleLowerCase());
      this._groups.splice(sourceGroupIndex, 1, sourceGroup);

      this._executionStatus = this._COMMAND_STATE_EXECUTED;

      this._commandResult$.next(this._groups);
    });
  }

  override undo(): void {
    if (this._executionStatus !== this._COMMAND_STATE_EXECUTED) return;

    const theGroup = this._groups.find((group: GroupModel) => group.id.toLowerCase() === this._targetGroup.id.toLowerCase());
    if (!theGroup) return;

    this._executionStatus = this._COMMAND_STATE_EXECUTING;

    let newGroupIndex: number = 1;
    const targetGroup = this._groups.find(g => g.id.toLowerCase() === this._respondent.groupId.toLowerCase());
    if (!targetGroup) throw new Error('Assertion - the source group must exist');
    const setRespondents = targetGroup.respondents.filter(r => r.isSet);

    const respondentsToMakeWay = targetGroup.respondents.filter(r => r.groupIndex >= this._respondent.groupIndex && r.isSet);

    if (setRespondents.length > 0) {
      newGroupIndex = Math.max(...setRespondents.map(r => r.groupIndex)) + 1;
    }

    const movedRespondent = theGroup.respondents.find(r => r.id?.toLowerCase() === this._respondent.id?.toLowerCase());
    const respondentsToReIndex = theGroup.respondents
      .filter((r: RespondentModel) => r.groupIndex > movedRespondent!.groupIndex && r.isSet);

    const dtos: any[] = [];
    respondentsToMakeWay.forEach(r => dtos.push({
      commandId: ++MoveRespondentCommand._commandId,
      identifier: r.id,
      commandType: CommandTypes.update,
      target: r,
      propertyNames: ['groupIndex'],
      newValues: [ r.groupIndex + 1]
    }));
    dtos.push({
      commandId: ++MoveRespondentCommand._commandId,
      identifier: this._respondent.id,
      commandType: CommandTypes.update,
      target: this._respondent,
      propertyNames: ['groupId', 'groupIndex'],
      newValues: [targetGroup.id, this._respondent.groupIndex]
    });
    respondentsToReIndex.forEach(r => dtos.push({
      commandId: ++MoveRespondentCommand._commandId,
      identifier: r.id,
      commandType: CommandTypes.update,
      target: r,
      propertyNames: ['groupIndex'],
      newValues: [r.groupIndex - 1]
    }));

    this._appService.setLoading(true);

    this._httpClient.post<CommandResult[]>(this._apiUri, dtos).pipe(
      retryBackoff(environment.retryConfig),
      catchError(this._error.bind(this))
    ).subscribe((results: CommandResult[] | CommandHandlerResult) => {

      this._appService.setLoading(false);

      if (Array.isArray(results)) {
        const isSuccess = results.every(r => r.isSuccessful);
        const errorMessage = isSuccess ? '' : this._translateService.instant('v2GeneralCommandErrorMessage');
        if (!isSuccess) {
          this._commandResult$.next([]);
          this._executionStatus = this._COMMAND_STATE_ERROR;
          this._appService.setLastError({ errorMessage: errorMessage } as ApplicationError);
          this._executionStatus = this._COMMAND_STATE_ERROR;

          return;
        }
      }

      const targetGroup = this._revertRespondentMoveFromSourceGroup(this._groups.find(g => g.id.toLowerCase() === this._respondent.groupId.toLowerCase())!);
      const sourceGroup = this._removeRespondentFromSourceGroup(this._targetGroup);

      const targetGroupIndex = this._groups.findIndex(g => g.id.toLowerCase() === targetGroup.id.toLowerCase());
      this._groups.splice(targetGroupIndex, 1, targetGroup);
      const sourceGroupIndex = this._groups.findIndex(g => g.id.toLowerCase() === sourceGroup.id.toLocaleLowerCase());
      this._groups.splice(sourceGroupIndex, 1, sourceGroup);

      this._executionStatus = this._COMMAND_STATE_NOT_EXECUTED;

      this._commandResult$.next(this._groups);
    });
  }

  private _moveRespondentIntoTheTargetGroup(group: GroupModel, newGroupIndex: number): GroupModel {
    const theRespondent = structuredClone(this._respondent);
    const theGroup = structuredClone(group);

    theRespondent.groupId = this._targetGroup.id;
    theRespondent.groupIndex = newGroupIndex;
    theGroup.respondents.splice(newGroupIndex - 1, 1, theRespondent);

    return theGroup;
  }

  private _revertRespondentMoveFromSourceGroup(group: GroupModel) {
    const theRespondent = structuredClone(this._respondent);
    const theGroup = structuredClone(group);

    theRespondent.groupId = theGroup.id;
    theGroup.respondents.pop();
    theGroup.respondents.forEach(r => {
      if (r.groupIndex >= theRespondent.groupIndex) r.groupIndex++;
    })
    theGroup.respondents.splice(theRespondent.groupIndex - 1, 0, theRespondent);

    return theGroup;
  }
}
